import { z } from 'zod'
import type { RedactedValue } from './privacy.js'

/**
 * The audit log: one row per business action (create, post, void, login, …), written in the same
 * transaction as the change. Read-only; newest first. Dates filter by business day (Asia/Muscat).
 */

const entityType = z
  .string()
  .trim()
  .regex(/^[A-Za-z][A-Za-z0-9_]{0,63}$/, 'Invalid entity type')

const auditPageFields = {
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
}

export const auditLogQuerySchema = z.object({
  ...auditPageFields,
  entityType: entityType.optional(),
  entityId: z.string().trim().min(1).max(100).optional(),
  actorUserId: z.uuid().optional(),
  action: z
    .string()
    .trim()
    .regex(/^[A-Za-z][A-Za-z0-9_]{0,49}$/, 'Invalid action')
    .transform((v) => v.toUpperCase())
    .optional(),
  /** Business dates (Asia/Muscat), inclusive. */
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
})
export type AuditLogQuery = z.input<typeof auditLogQuerySchema>

/** `GET /audit-log/entity/:type/:id` — the history of one record. */
export const auditEntityParamsSchema = z.object({
  type: entityType,
  id: z.string().trim().min(1).max(100),
})
export const auditEntityQuerySchema = z.object(auditPageFields)
export type AuditEntityQuery = z.input<typeof auditEntityQuerySchema>

export interface AuditLogEntry {
  id: string
  occurredAt: string
  /** CREATE, UPDATE, POST, VOID, LOGIN, … */
  action: string
  entityType: string
  entityId: string | null
  /**
   * What the record is called today: a document's number (null while it is a draft), or a master
   * record's name (with its code for customers, suppliers, accounts and tills). Null for entity types
   * without one (settings) and for records that no longer exist.
   */
  entityLabel: string | null
  /** Null for system jobs and data migration. */
  actor: { id: string; name: string; email: string } | null
  /**
   * Changed fields only; credential-like fields are never included. A personal value removed by
   * anonymisation reads as the `{ $redacted: true }` marker (`RedactedValue`, privacy.ts).
   */
  changes: {
    before?: Record<string, unknown | RedactedValue>
    after?: Record<string, unknown | RedactedValue>
  } | null
  context: Record<string, unknown> | null
  /**
   * Request correlation and client address: present only for user administrators
   * (`users.manage`), who investigate sign-ins and sessions. Omitted for everyone else.
   */
  requestId?: string | null
  ip?: string | null
}

// ── vocabulary ───────────────────────────────────────────────────────────────

/**
 * Every action the API writes to the audit log. `AuditService.record` and the `audit_log.action` column
 * take only these, so a module that records a new action adds it here (the API does not compile otherwise),
 * and the Audit log screen then needs its name in English and Arabic (`features/audit/audit.test.tsx`).
 * Rows of the legacy import keep whatever they were written with; readers treat `action` as text.
 */
export const AUDIT_ACTIONS = [
  'CREATE',
  'UPDATE',
  'DELETE',
  'POST',
  'VOID',
  'ALLOCATE',
  'UNALLOCATE',
  'APPROVE',
  'REJECT',
  'CONVERT',
  'ARCHIVE',
  'RESTORE',
  'SET_MAIN',
  'OPEN',
  'CLOSE',
  'REOPEN',
  'REVERSE',
  'MATCH',
  'UNMATCH',
  'ADJUST',
  'COMPLETE',
  'PAY_IN',
  'PAY_OUT',
  'LOGIN',
  'LOGIN_FAILED',
  'LOGIN_LOCKED',
  'LOGOUT',
  'UNLOCK',
  'PASSWORD_CHANGE',
  'PASSWORD_RESET',
  'SET',
  'ATTACH',
  'DETACH',
  'SUBMIT',
  'RETURN',
  'WITHDRAW',
  'CANCEL',
  'REASSIGN',
  'REFRESH_APPROVERS',
  'RETRY',
  'ACTION_FAILED',
  'SAVE_DRAFT',
  'DISCARD_DRAFT',
  'PUBLISH',
  'ENABLE',
  'DISABLE',
  'REORDER',
  'DUPLICATE',
  'REVOKE',
  'MOVE',
  'EMAILED',
  'ISSUE',
  'INVITED',
  'INVITATION_ACCEPTED',
  'EXPORT',
  'IMPORT',
  'ANONYMISE',
  'LIFT',
  'VERIFY',
  'START',
  'REFUSE',
  // The company itself: the legacy import, going live, and a super admin signing in as a user.
  'MIGRATE',
  'ACTIVATE',
  'IMPERSONATION_START',
  // Quotations.
  'MARK_LOST',
  'START_PROJECT',
  // Tasks.
  'COMMIT',
  'BACKLOG',
  'PAUSE',
  'ACCEPT',
  'ASSIGN',
  'CLAIM',
  'BLOCK',
  'UNBLOCK',
  'REQUEST_DATE',
  'ADD_PERSON',
  'REMOVE_PERSON',
  'LINK',
  'UNLINK',
  // Service jobs, visits, follow-ups and collections.
  'WAITING',
  'ITEM_ADD',
  'ITEM_UPDATE',
  'ITEM_REMOVE',
  'PRICE_APPROVAL',
  'SCHEDULE',
  'RESCHEDULE',
  'BEGIN_WORK',
  'WORK',
  'FINISH',
  'REQUEST',
  'RESUBMIT',
  'INVOICE',
  'REPORT',
  // Service sites and assets.
  'GENERATE_PLACES',
  'DUPLICATE_PLACE',
  'ASSETS_ADD',
  'ASSETS_COPY',
  'ASSETS_IMPORT',
  'STATUS',
  'REPLACE',
  // Projects: lifecycle, team, notes and the plan.
  'HOLD',
  'RESUME',
  'UPDATE_FEATURES',
  'ADD_MEMBER',
  'UPDATE_MEMBER',
  'REMOVE_MEMBER',
  'ADD_NOTE',
  'PIN_NOTE',
  'UNPIN_NOTE',
  'REMOVE_NOTE',
  'ADD_PRESETS',
  'ADD_NODE',
  'UPDATE_NODE',
  'MOVE_NODE',
  'REMOVE_NODE',
  'ACHIEVE_MILESTONE',
  'UNACHIEVE_MILESTONE',
  'PROGRESS',
  'APPLY_TEMPLATE',
  'PLACE_TASK',
  'APPLY_PLAN',
  // Projects: budgets, billing, rates, alerts and status reports.
  'BUDGET_CREATE',
  'BUDGET_UPDATE',
  'BUDGET_DELETE',
  'BUDGET_SUBMIT',
  'BUDGET_APPROVE',
  'BUDGET_RETURN',
  'BILLING_ITEM_CREATE',
  'BILLING_ITEM_UPDATE',
  'BILLING_ITEM_DELETE',
  'BILLING_ITEM_CANCEL',
  'BILLING_ITEM_READY',
  'BILLING_ITEM_UNREADY',
  'BILLING_ITEMS_ORDER',
  'BILLING_OVERRIDE',
  'BILL',
  'BILL_VOID',
  'ADD_RATE',
  'REPLACE_RATE',
  'SIGNAL_ACKNOWLEDGE',
  'SIGNAL_UNACKNOWLEDGE',
  'REFRESH_FIGURES',
  'FINALISE',
  // Projects: the contract, its BOQ, variations, claims, retention and guarantees.
  'CONTRACT_CREATE',
  'CONTRACT_UPDATE',
  'CONTRACT_ACTIVATE',
  'CONTRACT_AMEND',
  'CONTRACT_PRACTICAL_COMPLETION',
  'CONTRACT_CLOSE',
  'CONTRACT_ADVANCE',
  'CONTRACT_ADVANCE_VOID',
  'CONTRACT_ADVANCE_REDUCE',
  'CONTRACT_ADVANCE_REDUCE_VOID',
  'BOQ_VERSION_CREATE',
  'BOQ_VERSION_UPDATE',
  'BOQ_VERSION_DELETE',
  'BOQ_IMPORT',
  'BOQ_BUDGET',
  'VARIATION_CREATE',
  'VARIATION_UPDATE',
  'VARIATION_REQUEST_APPROVAL',
  'VARIATION_APPROVE_INTERNAL',
  'VARIATION_RETURN_INTERNAL',
  'VARIATION_SUBMIT',
  'VARIATION_APPROVE',
  'VARIATION_REJECT',
  'VARIATION_WITHDRAW',
  'CLAIM_CREATE',
  'CLAIM_UPDATE',
  'CLAIM_DELETE',
  'CLAIM_REQUEST_APPROVAL',
  'CLAIM_APPROVE_INTERNAL',
  'CLAIM_RETURN_INTERNAL',
  'CLAIM_SUBMIT',
  'CLAIM_REJECT',
  'CLAIM_CERTIFIED_FIGURES',
  'CLAIM_CERTIFY',
  'CLAIM_VOID',
  'RETENTION_RELEASE',
  'RETENTION_RELEASE_VOID',
  'RETENTION_FORFEIT',
  'RETENTION_FORFEIT_VOID',
  'GUARANTEE_CREATE',
  'GUARANTEE_UPDATE',
  'GUARANTEE_EVENT',
  'GUARANTEE_EXPIRE',
  // Books health.
  'DISMISS',
  'UNDISMISS',
  // The AI assistant.
  'KEY_CHECK',
  'RUN',
  // Recurring expenses: a date dismissed instead of posted, the next date created early, a schedule ended.
  'SKIP',
  'RUN_NOW',
  'END',
  // Loyalty points: a customer joining the programme.
  'ENROLL',
  // Maintenance contracts (Service C2–C4): lifecycle, plans and their generator, per-asset checks, billing.
  'TERMINATE',
  'RENEW',
  'EXPIRE',
  'PLAN_ADD',
  'PLAN_UPDATE',
  'GENERATE',
  'CHECK',
  'FAULT',
  'BILLING_REBUILD',
  // Cheques (ADR-046): their steps; a cancel is CANCEL, a mistake taken back UNDO, a cleared cheque the bank
  // took back CHARGEBACK.
  'DEPOSIT',
  'CLEAR',
  'BOUNCE',
  'HAND_BACK',
  'CHARGEBACK',
  'UNDO',
] as const
export type AuditAction = (typeof AUDIT_ACTIONS)[number]

/**
 * Every record type the API writes to the audit log (`audit_log.entity_type`), kept the same way as
 * `AUDIT_ACTIONS`. The parts of a project (members, plan rows, notes, budgets, the contract and its claims)
 * are audited under `Project`.
 */
export const AUDIT_ENTITY_TYPES = [
  'SalesInvoice',
  'Quotation',
  'Receipt',
  'CreditNote',
  'CustomerRefund',
  'Customer',
  'CustomerGroup',
  'Purchase',
  'SupplierPayment',
  'SupplierReturn',
  'SupplierRefund',
  'Supplier',
  'SupplierGroup',
  'PosTerminal',
  'PosShift',
  'PosSale',
  'PosReturn',
  'Product',
  'ProductCategory',
  'ProductAttribute',
  'Unit',
  'CustomPrice',
  'Warehouse',
  'StockAdjustment',
  'StockTransfer',
  'Account',
  'AccountRoles',
  'JournalEntry',
  'FiscalYear',
  'TaxRate',
  'TreasuryAccount',
  'PaymentMethod',
  'TreasuryTransfer',
  'BankStatement',
  'BookFinding',
  'Expense',
  'ExpenseCategory',
  'RecurringExpense',
  'User',
  'Role',
  'OrgUnit',
  'WorkLocation',
  'CalendarHoliday',
  'Tenant',
  'CompanyProfile',
  'SalesSettings',
  'SettingGroup',
  'TenantSecret',
  'LockDate',
  'Workflow',
  'WorkflowRun',
  'WorkflowDelegation',
  'Message',
  'MessageTemplate',
  'MessageOptOut',
  'IssuedDocument',
  'PublicLink',
  'RetentionRule',
  'LegalHold',
  'PrivacyRequest',
  'PrivacySubject',
  'UserInvitation',
  // Tasks and teams.
  'Task',
  'TaskTeam',
  // Service.
  'ServiceJob',
  'ServiceVisit',
  'ServiceFollowUp',
  'ServiceCollection',
  'ServiceSite',
  'ServicePlace',
  'ServiceSiteTemplate',
  'ServiceAsset',
  'ServiceAssetType',
  // Maintenance contracts (plans, occurrences and instalments are audited on their contract).
  'ServiceContract',
  // Projects.
  'Project',
  'ProjectType',
  'ProjectCostCategory',
  'ProjectCostCategoryMap',
  'ProjectCostEntry',
  'ProjectMaterialIssue',
  'ProjectRole',
  'ProjectPersonRate',
  'ProjectTimeEntry',
  'ProjectStatusReport',
  'ProjectVariation',
  'ProjectClaim',
  'ProjectGuarantee',
  // The AI assistant.
  'AiSettings',
  'AiConversation',
  'AiRun',
  // Loyalty points.
  'LoyaltyProgram',
  'LoyaltyTier',
  'LoyaltyRule',
  'LoyaltyMember',
  // Cheque follow-up (ADR-046).
  'Cheque',
  // Human resources and payroll (ADR-048). Contracts, salaries and status events are audited on the employee.
  'Employee',
  'EmployeeDocument',
  'PayComponent',
  'PayrollSettings',
  'ContributionRule',
  'PayrollRun',
  // Sales commission (ADR-049): members are audited on their plan, statements and payments on their run.
  'CommissionPlan',
  'CommissionRun',
] as const
export type AuditEntityType = (typeof AUDIT_ENTITY_TYPES)[number]
